import { useEffect, useState, type ReactNode } from 'react';
import { TripMap, type MapFocus } from '../map/TripMap.js';
import { ItineraryPanel } from '../panels/ItineraryPanel.js';
import { PlacePanel } from '../panels/PlacePanel.js';
import { PoolPanel } from '../panels/PoolPanel.js';
import { PlaceSearch } from '../search/PlaceSearch.js';
import { useStore } from '../store/StoreContext.js';
import { itineraryPlaces, poolPlaces, voteScore } from '../store/selectors.js';

type Tab = 'pool' | 'itinerary';

const CONNECTION_LABEL: Record<string, string> = {
  idle: 'hors ligne',
  connecting: 'connexion...',
  live: 'en direct',
  retrying: 'reconnexion...',
};

export function MapScreen(): ReactNode {
  const store = useStore();
  const { state, me, connection, banner, setBanner, addPlace } = store;

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [addMode, setAddMode] = useState(false);
  const [tab, setTab] = useState<Tab>('pool');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [focus, setFocus] = useState<MapFocus | null>(null);

  const selected = state.places.find((place) => place.id === selectedId) ?? null;
  const pool = poolPlaces(state);
  const steps = itineraryPlaces(state);

  // Un lieu supprime par quelqu'un d'autre pendant qu'il est selectionne
  // doit liberer la selection, sinon la feuille reste ouverte sur du vide.
  useEffect(() => {
    if (selectedId !== null && selected === null) setSelectedId(null);
  }, [selectedId, selected]);

  // Le bandeau est informatif, pas bloquant : il s'efface tout seul.
  useEffect(() => {
    if (banner === null) return;
    const timer = window.setTimeout(() => setBanner(null), 6000);
    return () => window.clearTimeout(timer);
  }, [banner, setBanner]);

  function select(placeId: number): void {
    setSelectedId(placeId);
    setSheetOpen(true);
  }

  async function pick(lat: number, lng: number): Promise<void> {
    setAddMode(false);
    try {
      const place = await addPlace({
        lat,
        lng,
        title: 'Nouveau lieu',
        description: '',
        category: 'autre',
        url: null,
      });
      select(place.id);
    } catch {
      setBanner("Le lieu n a pas pu etre cree.");
    }
  }

  const handleLabel = selected !== null ? selected.title : sheetOpen ? 'Reduire' : 'Vivier et itineraire';

  return (
    <div className={`app${sheetOpen ? ' is-sheet-open' : ''}${addMode ? ' is-adding' : ''}`}>
      <header className="app__bar">
        <div className="app__brand">
          <h1 className="app__brandname">Carnet de route</h1>
          <p className="app__brandsub">
            Asie centrale <span aria-hidden="true">·</span> 42°30′N 68°30′E
          </p>
        </div>

        <span className={`app__status app__status--${connection}`}>
          <span className="app__statusdot" aria-hidden="true" />
          {CONNECTION_LABEL[connection] ?? connection}
        </span>

        <span className="app__me" title={me?.pseudo ?? ''}>
          <span
            className="app__meswatch"
            aria-hidden="true"
            style={{ background: me?.color ?? 'var(--zero)' }}
          />
          {me?.pseudo ?? ''}
        </span>

        <button
          className={`button button--primary button--add${addMode ? ' is-active' : ''}`}
          type="button"
          onClick={() => setAddMode((current) => !current)}
        >
          <span className="button__icon" aria-hidden="true">
            {addMode ? '×' : '+'}
          </span>
          <span className="button__text">{addMode ? 'Annuler' : 'Ajouter un lieu'}</span>
        </button>
      </header>

      {banner !== null ? (
        <p className="app__banner" role="status">
          <span className="app__bannertext">{banner}</span>
          <button className="button button--ghost" type="button" onClick={() => setBanner(null)}>
            OK
          </button>
        </p>
      ) : null}

      <div className="app__body">
        <div className="app__map">
          <TripMap
            places={state.places}
            itinerary={steps}
            scoreOf={(placeId) => voteScore(state, placeId)}
            selectedId={selectedId}
            onSelect={select}
            addMode={addMode}
            onPick={(lat, lng) => void pick(lat, lng)}
            focus={focus}
          />

          {/* La recherche ne fait que cadrer la carte. Aucun lieu n'est cree
              ici : le point se pose ensuite avec le mode d'ajout, inchange. */}
          <PlaceSearch
            onPick={(entry) => {
              setFocus({
                lat: entry.lat,
                lng: entry.lng,
                zoom: entry.zoom,
                token: Date.now(),
              });
              // La feuille mobile se replie : on vient de demander a voir la
              // carte, la lui cacher juste apres serait absurde.
              setSheetOpen(false);
            }}
          />

          {state.places.length === 0 && !addMode ? (
            <div className="mapempty">
              <div className="mapempty__card">
                <svg className="mapempty__mark" viewBox="0 0 64 64" aria-hidden="true">
                  <circle cx="32" cy="32" r="26" className="mapempty__ring" />
                  <circle cx="32" cy="32" r="17" className="mapempty__ring" />
                  <path d="M32 6v52M6 32h52" className="mapempty__cross" />
                  <path d="M32 20l5 10 10 2-8 7 2 11-9-5-9 5 2-11-8-7 10-2z" className="mapempty__star" />
                </svg>
                <h2 className="mapempty__title">La carte est vierge</h2>
                <p className="mapempty__text">
                  Une yourte au Kirghizistan, un bazar a Boukhara, un col a 3 000 m. Pose le premier
                  repere, les autres suivront.
                </p>
                <button
                  className="button button--primary"
                  type="button"
                  onClick={() => setAddMode(true)}
                >
                  Poser le premier lieu
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <aside className={`app__side${sheetOpen ? ' is-open' : ''}`}>
          <button
            className="app__handle"
            type="button"
            aria-expanded={sheetOpen}
            onClick={() => setSheetOpen((current) => !current)}
          >
            <span className="app__grab" aria-hidden="true" />
            <span className="app__handletext">{handleLabel}</span>
            {selected === null ? (
              <span className="app__handlecount">{pool.length + steps.length}</span>
            ) : null}
          </button>

          {selected !== null ? (
            <PlacePanel place={selected} onClose={() => setSelectedId(null)} />
          ) : (
            <>
              <nav className="tabs">
                <button
                  className={`tab${tab === 'pool' ? ' is-current' : ''}`}
                  type="button"
                  onClick={() => setTab('pool')}
                >
                  Vivier
                  <span className="tab__count">{pool.length}</span>
                </button>
                <button
                  className={`tab${tab === 'itinerary' ? ' is-current' : ''}`}
                  type="button"
                  onClick={() => setTab('itinerary')}
                >
                  Itineraire
                  <span className="tab__count">{steps.length}</span>
                </button>
              </nav>
              {tab === 'pool' ? (
                <PoolPanel selectedId={selectedId} onSelect={select} />
              ) : (
                <ItineraryPanel selectedId={selectedId} onSelect={select} />
              )}
            </>
          )}
        </aside>
      </div>
    </div>
  );
}
